import { config } from "../config";
import { TSession } from "../utils/types";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";

const resolveCommunicationsApiProtocol = (): string => {
  const overrideProtocol = (
    process.env.EXPO_PUBLIC_COMMUNICATIONS_API_PROTOCOL || ""
  )
    .trim()
    .toLowerCase();
  return overrideProtocol || config.coreApi.protocol;
};

const resolveCommunicationsApiHost = (): string => {
  const overrideHost = (
    process.env.EXPO_PUBLIC_COMMUNICATIONS_API_HOST || ""
  ).trim();
  return overrideHost || config.coreApi.host;
};

const protocol = resolveCommunicationsApiProtocol();
const host = resolveCommunicationsApiHost();
const urlPrefixCommunications = `${protocol}://${host}/clinics/v1/communications`;
const urlPrefixDocumentation = `${protocol}://${host}/clinics/v1/documentation`;

const assertClinicId = (session: TSession): string => {
  if (!session.clinicId) {
    throw new Error("Missing clinic ID in session.");
  }
  return session.clinicId;
};

const buildCallAttemptHeaders = (
  callAttemptId?: string | null,
): Record<string, string> | undefined => {
  const normalizedCallAttemptId = (callAttemptId || "").trim();
  if (!normalizedCallAttemptId) {
    return undefined;
  }
  return {
    "X-Call-Attempt-Id": normalizedCallAttemptId,
  };
};

const throwHttpError = async (response: Response, context: string) => {
  const errorText = await response.text().catch(() => "");
  const error = new Error(
    `${context}: ${response.status} - ${errorText}`,
  ) as Error & {
    status?: number;
    responseText?: string;
    context?: string;
  };
  error.status = response.status;
  error.responseText = errorText;
  error.context = context;
  throw error;
};

const parseJsonOrEmptyObject = async <T>(
  response: Response,
): Promise<T | Record<string, never>> => {
  const responseText = await response.text().catch(() => "");
  if (!responseText) {
    return {};
  }

  try {
    return JSON.parse(responseText) as T;
  } catch {
    return {};
  }
};

const toObjectRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object") {
    return null;
  }
  return value as Record<string, unknown>;
};

const toStringValue = (value: unknown): string => {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
};

const toNumberValue = (value: unknown): number | null => {
  const parsedValue =
    typeof value === "string" ? Number(value.trim()) : Number(value);
  if (!Number.isFinite(parsedValue)) {
    return null;
  }
  return parsedValue;
};

const toBooleanValue = (value: unknown): boolean | null => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === "true") {
      return true;
    }
    if (normalizedValue === "false") {
      return false;
    }
  }
  return null;
};

const resolveInboundRoutingCandidate = (
  payload: unknown,
): Record<string, unknown> => {
  const rootCandidate = toObjectRecord(payload);
  if (!rootCandidate) {
    return {};
  }

  const dataCandidate = toObjectRecord(rootCandidate.data);
  const candidatePool = [
    rootCandidate,
    toObjectRecord(rootCandidate.inbound_voice_routing),
    toObjectRecord(rootCandidate.inboundVoiceRouting),
    toObjectRecord(rootCandidate.routing),
    dataCandidate,
    toObjectRecord(dataCandidate?.inbound_voice_routing),
    toObjectRecord(dataCandidate?.inboundVoiceRouting),
    toObjectRecord(dataCandidate?.routing),
  ].filter((candidate): candidate is Record<string, unknown> => !!candidate);

  const preferredCandidate =
    candidatePool.find((candidate) => {
      const hasRoutingKeys =
        "enabled" in candidate ||
        "strategy" in candidate ||
        "timeout_seconds" in candidate ||
        "timeoutSeconds" in candidate ||
        Array.isArray(candidate.members) ||
        Array.isArray(candidate.targets) ||
        Array.isArray(candidate.routes);
      return hasRoutingKeys;
    }) || rootCandidate;

  return preferredCandidate;
};

const parseInboundVoiceRoutingConfiguration = (
  payload: unknown,
): TInboundVoiceRoutingConfiguration => {
  const candidate = resolveInboundRoutingCandidate(payload);

  const rawMembers =
    (Array.isArray(candidate.members) ? candidate.members : null) ||
    (Array.isArray(candidate.targets) ? candidate.targets : null) ||
    (Array.isArray(candidate.routes) ? candidate.routes : null) ||
    (Array.isArray(candidate.destinations) ? candidate.destinations : null) ||
    [];

  const members: TInboundVoiceRoutingMember[] = rawMembers
    .map((memberPayload, index) => {
      const memberCandidate = toObjectRecord(memberPayload);
      if (!memberCandidate) {
        return null;
      }

      const normalizedTargetType =
        toStringValue(memberCandidate.target_type) ||
        toStringValue(memberCandidate.targetType) ||
        toStringValue(memberCandidate.type);
      const targetType =
        normalizedTargetType === "phone_e164" ||
        normalizedTargetType === "phone" ||
        normalizedTargetType === "phone_number"
          ? "phone_e164"
          : normalizedTargetType === "clinic_user" ||
              normalizedTargetType === "user" ||
              normalizedTargetType === "clinicUser"
            ? "clinic_user"
            : null;

      if (!targetType) {
        return null;
      }

      const targetValue =
        toStringValue(memberCandidate.target_value) ||
        toStringValue(memberCandidate.targetValue) ||
        (targetType === "phone_e164"
          ? toStringValue(memberCandidate.e164)
          : toStringValue(memberCandidate.user_uuid));
      if (!targetValue) {
        return null;
      }

      const priority = toNumberValue(memberCandidate.priority);
      const weight = toNumberValue(memberCandidate.weight);
      const active = toBooleanValue(memberCandidate.active);

      return {
        target_type: targetType,
        target_value: targetValue,
        priority: Number.isFinite(priority) ? Math.trunc(priority || 0) : index,
        weight: Number.isFinite(weight)
          ? Math.max(1, Math.trunc(weight || 1))
          : 1,
        active: active === null ? true : active,
      } as TInboundVoiceRoutingMember;
    })
    .filter((member): member is TInboundVoiceRoutingMember => !!member);

  const strategyValue =
    toStringValue(candidate.strategy).toLowerCase() ||
    toStringValue(candidate.routing_strategy).toLowerCase() ||
    toStringValue(candidate.mode).toLowerCase();
  const timeoutSecondsValue =
    toNumberValue(candidate.timeout_seconds) ??
    toNumberValue(candidate.timeoutSeconds) ??
    toNumberValue(candidate.timeout) ??
    30;
  const enabledValue =
    toBooleanValue(candidate.enabled) ?? (members.length > 0 ? true : false);

  return {
    enabled: enabledValue,
    strategy: strategyValue === "simultaneous" ? "simultaneous" : "sequential",
    timeout_seconds: Number.isFinite(timeoutSecondsValue)
      ? Math.max(1, Math.min(120, Math.trunc(timeoutSecondsValue)))
      : 30,
    members,
  };
};

export type TCommunicationsCallId = string;

export type TOutboundFromNumbersResponse = {
  from_numbers: string[];
  default_from_e164: string | null;
};

export type TCommunicationsPhoneNumberPricing = {
  plan_code?: string | null;
  monthly_price_cents: number;
  currency: string;
  included_number_count: number;
  included_minutes: number;
  extra_number_monthly_price_cents: number;
  user_seat_monthly_price_cents?: number | null;
  overage_minute_price_cents: number;
  setup_price_cents?: number | null;
  display_label?: string | null;
};

export type TCommunicationsPhoneNumberInventoryItem = {
  phone_number_id: number;
  e164: string;
  friendly_name?: string | null;
  capabilities?: string[] | Record<string, boolean> | null;
  status?: string | null;
  plan_code?: string | null;
  monthly_price_cents?: number | null;
  currency?: string | null;
  display_label?: string | null;
};

export type TListAvailablePhoneNumbersResponse = {
  results: TCommunicationsPhoneNumberInventoryItem[];
  total: number;
};

export type TCommunicationsAssignedPhoneNumberItem = {
  phone_number_id: number;
  e164: string;
  friendly_name?: string | null;
  capabilities?: string[] | Record<string, boolean> | null;
  status?: string | null;
  is_primary?: boolean;
  assigned_at?: string | null;
  monthly_price_cents?: number | null;
  currency?: string | null;
};

export type TListAssignedPhoneNumbersResponse = {
  results: TCommunicationsAssignedPhoneNumberItem[];
  total: number;
};

export type TClaimAvailablePhoneNumberRequest = {
  make_primary?: boolean;
};

export type TClaimAvailablePhoneNumberResponse = {
  phone_number_id: number;
  e164: string;
  assignment_id?: number | null;
  is_primary?: boolean;
};

export type TSetPrimaryPhoneNumberResponse = {
  phone_number_id: number;
  e164?: string | null;
  is_primary?: boolean;
};

export type TUnassignPhoneNumberResponse = {
  phone_number_id: number;
  e164?: string | null;
  unassigned?: boolean;
};

export type TInboundVoiceRoutingTargetType = "phone_e164" | "clinic_user";

export type TInboundVoiceRoutingStrategy = "sequential" | "simultaneous";

export type TInboundVoiceRoutingMember = {
  target_type: TInboundVoiceRoutingTargetType;
  target_value: string;
  priority: number;
  weight: number;
  active: boolean;
};

export type TInboundVoiceRoutingConfiguration = {
  enabled: boolean;
  strategy: TInboundVoiceRoutingStrategy;
  timeout_seconds: number;
  members: TInboundVoiceRoutingMember[];
};

export type TGetCommunicationsUsageRequest = {
  start_date?: string;
  end_date?: string;
  group_by?: "day" | "week" | "month";
};

export type TCommunicationsUsageBucket = {
  period_start: string;
  period_end: string;
  total_calls: number;
  completed_calls: number;
  total_duration_seconds: number;
  total_billable_minutes: number;
  included_minutes?: number;
  overage_minutes?: number;
};

export type TGetCommunicationsUsageResponse = {
  total_calls: number;
  completed_calls: number;
  total_duration_seconds: number;
  total_billable_minutes: number;
  included_minutes?: number;
  overage_minutes?: number;
  period_start: string | null;
  period_end: string | null;
  buckets?: TCommunicationsUsageBucket[];
};

export type TCommunicationsClinicUserAccess = {
  clinic_user_id: number;
  user_uuid: string;
  role: string;
  communications_enabled: boolean;
  is_billable_communications_seat: boolean;
};

export type TListCommunicationsClinicUsersAccessResponse = {
  results: TCommunicationsClinicUserAccess[];
  total: number;
  billable_seat_count: number;
};

export type TUpdateCommunicationsClinicUserAccessResponse = {
  user: TCommunicationsClinicUserAccess;
  billable_seat_count: number;
};

export type TMintCommunicationsTokenRequest = {
  token_type?: "browser_subscriber";
  expires_in_minutes?: number;
};

export type TMintCommunicationsTokenResponse = {
  project: string;
  jwt_token: string;
  token?: string | null;
  expires_in_minutes: number;
};

export const resolveSignalWireToken = (
  response: TMintCommunicationsTokenResponse,
): string => {
  const normalizedToken =
    (response.token || "").trim() || (response.jwt_token || "").trim();
  if (!normalizedToken) {
    throw new Error("Communications token response is missing token.");
  }
  return normalizedToken;
};

export type TCreateOutboundCallRequest = {
  client_request_id: string;
  patient_uuid?: string | null;
  to_e164: string;
  from_e164: string;
  agent_client_identity?: string | null;
  agent_e164?: string | null;
};

export type TCreateOutboundCallResponse = {
  call_uuid: TCommunicationsCallId;
  call_sid: string;
  status: string;
};

export type TCreateOutboundMessageRequest = {
  client_request_id: string;
  from_e164: string;
  to_e164: string;
  body: string;
  patient_uuid?: string | null;
};

export type TCreateOutboundMessageResponse = {
  message_uuid: string;
  conversation_uuid: string;
  status: string;
  provider_message_sid: string | null;
  client_request_id: string | null;
};

export type TCommunicationsConversation = {
  conversation_uuid: string;
  clinic_uuid: string;
  patient_uuid: string | null;
  participant_e164: string;
  from_e164?: string | null;
  to_e164?: string | null;
  clinic_e164?: string | null;
  assigned_e164?: string | null;
  provider: string;
  is_archived: boolean;
  is_read?: boolean | null;
  read_at?: string | null;
  last_read_message_at?: string | null;
  last_message_preview?: string | null;
  last_message_body?: string | null;
  unread_count?: number | null;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
};

export type TCommunicationsMessage = {
  message_uuid: string;
  conversation_uuid: string;
  provider_message_sid: string | null;
  client_request_id: string | null;
  direction: string;
  status: string;
  status_reason: string | null;
  from_e164: string;
  to_e164: string;
  body: string;
  segment_count: number | null;
  price: string | null;
  price_unit: string | null;
  is_billable: boolean;
  billing_unit_count: number;
  provider_created_at: string | null;
  provider_updated_at: string | null;
  last_status_event_at: string | null;
  created_at: string;
  updated_at: string;
};

export type TListConversationsResponse = {
  results: TCommunicationsConversation[];
  total: number;
};

export type TGetConversationResponse = {
  conversation: TCommunicationsConversation;
};

export type TListConversationMessagesResponse = {
  results: TCommunicationsMessage[];
  total: number;
};

export type TCommunicationsCall = {
  call_uuid: TCommunicationsCallId;
  call_sid: string;
  parent_call_sid: string | null;
  client_request_id: string | null;
  patient_uuid: string | null;
  from_e164: string;
  from_phone_number_assignment_id: number | null;
  to_e164: string;
  agent_participant_type: string | null;
  agent_client_identity: string | null;
  agent_e164: string | null;
  direction: string;
  status: string;
  status_reason: string | null;
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  hangup_requested_at: string | null;
  hangup_requested_by_user_id: number | null;
  hangup_request_status: string | null;
  last_status_event_at: string | null;
  created_at: string;
  updated_at: string;
};

export type TListCallsResponse = {
  results: TCommunicationsCall[];
  total: number;
};

export type TCommunicationsRecording = {
  id: number;
  recording_sid: string;
  recording_url: string | null;
  duration_seconds: number | null;
  track: string;
  ingestion_status: string;
  s3_key: string | null;
  created_at: string;
};

export type TGetCallDetailResponse = {
  call: TCommunicationsCall;
  recordings: TCommunicationsRecording[];
};

export type THangupCallResponse = {
  call_uuid: TCommunicationsCallId;
  call_sid: string;
  status: string;
  hangup_request_status: string | null;
};

export type TCommunicationsRecordingListItem = {
  id: number;
  recording_sid: string;
  call_uuid: TCommunicationsCallId;
  call_sid: string;
  patient_uuid: string | null;
  from_e164: string;
  to_e164: string;
  duration_seconds: number | null;
  ingestion_status: string;
  transcription_status: string;
  s3_key: string | null;
  created_at: string;
};

export type TListRecordingsRequest = {
  limit?: number;
  offset?: number;
  call_uuid?: TCommunicationsCallId;
  patient_uuid?: string;
  ingestion_status?: string;
  transcription_status?: string;
};

export type TListRecordingsResponse = {
  results: TCommunicationsRecordingListItem[];
  total: number;
};

export type TCommunicationsCallNoteAudioInfo = {
  id: number;
  s3_key: string | null;
  get_url: string | null;
  type: string;
  encounter_type: string | null;
  duration_in_seconds: number | null;
  transcription_s3_key: string | null;
  transcription_get_url: string | null;
  date_of_recording: string | null;
};

export type TCommunicationsCallNote = {
  id: number;
  note: string;
  note_type: string;
  encounter_type: string | null;
  created_at: string;
  date_of_recording: string | null;
  audio_ids: number[];
  audio_info: TCommunicationsCallNoteAudioInfo[];
};

export type TGetCallNotesResponse = {
  call: TCommunicationsCall;
  notes: TCommunicationsCallNote[];
  total: number;
};

export type TAssignCallPatientRequest = {
  patient_uuid: string;
  bulk_update?: boolean;
  overwrite_existing_phone_number?: boolean | null;
};

export type TAssignCallPatientResponse = Record<string, unknown>;

export type TAssignConversationPatientRequest = {
  patient_uuid: string;
  bulk_update?: boolean;
  overwrite_existing_phone_number?: boolean | null;
};

export type TAssignConversationPatientResponse = Record<string, unknown>;

export type TDocumentationAudioMetadata = {
  id: number;
  s3_key: string;
  type: string;
  is_uploaded: boolean;
  created_at: string;
  date_of_recording: string | null;
  get_url: string;
  transcript_get_url: string | null;
};

export type TDocumentationAudioResponse = {
  metadata: TDocumentationAudioMetadata[];
};

export type TDocumentationAudioInfo = {
  id: number;
  s3_key: string | null;
  get_url: string | null;
  type: string;
  encounter_type: string | null;
  duration_in_seconds: number | null;
  transcription_s3_key: string | null;
  transcription_get_url: string | null;
  date_of_recording: string | null;
};

export type TDocumentationPatientNote = {
  id: number;
  note: string;
  note_json?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  note_type: string;
  encounter_type: string | null;
  created_at: string;
  date_of_recording: string | null;
  audio_ids?: number[];
  appointment_id?: number | null;
  audio_info?: TDocumentationAudioInfo[];
};

export type TPatientAudioGroup = {
  patient_uuid: string | null;
  patient_name: string | null;
  audio_files: TDocumentationAudioMetadata[];
};

export type TOrganizedAudioResponse = {
  audio_by_patient: TPatientAudioGroup[];
  total_patients: number;
  total_audio_files: number;
  uncategorized_count: number;
};

export type TPatientNotesGroup = {
  patient_uuid: string | null;
  patient_name: string | null;
  notes: TDocumentationPatientNote[];
};

export type TOrganizedNotesResponse = {
  notes_by_patient: TPatientNotesGroup[];
  total_patients: number;
  total_notes: number;
  uncategorized_count: number;
};

export type TNotesFeedMode = "chronological" | "grouped_by_patient";

export type TNotesFeedPatientName = {
  first_name: string | null;
  last_name: string | null;
  display_name: string;
};

export type TNotesFeedPatientInfo = {
  uuid: string;
  first_name: string;
  last_name: string;
  email: string | null;
};

export type TNotesFeedItem = {
  item_key: string;
  note_id: number;
  created_at: string;
  patient_uuid: string | null;
  patient_name: TNotesFeedPatientName;
  encounter_type: string | null;
  note_type: string;
  note: string;
  note_json: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  date_of_recording: string | null;
  audio_ids: number[];
  appointment_id: number | null;
  audio_info: TDocumentationAudioInfo[];
  is_signed: boolean;
  signed_at: string | null;
  signed_pdf_url: string | null;
  patient_info?: TNotesFeedPatientInfo | null;
};

export type TNotesFeedGroup = {
  group_label: string;
  group_type: "date" | "patient";
  item_ids: string[];
  children: TNotesFeedGroup[];
};

export type TNotesFeedAppliedFilters = {
  created_from: string | null;
  created_to: string | null;
  note_type: string[];
  patient_query: string | null;
  include_patient_info: boolean;
  include_signed_pdf_url: boolean;
};

export type TNotesFeedResponse = {
  mode: TNotesFeedMode;
  limit: number;
  items: TNotesFeedItem[];
  groups: TNotesFeedGroup[];
  next_cursor: string | null;
  applied_filters: TNotesFeedAppliedFilters;
};

type TGetClinicNotesFeedOptions = {
  mode?: TNotesFeedMode;
  limit?: number;
  cursor?: string;
  includePatientInfo?: boolean;
  includeSignedPdfUrl?: boolean;
  createdFrom?: string;
  createdTo?: string;
  noteType?: string[];
  patientQuery?: string;
};

export const getOutboundFromNumbers = async (
  session: TSession,
): Promise<TOutboundFromNumbersResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/from-numbers`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to fetch outbound from-numbers");
  }

  return response.json();
};

export const getPhoneNumberPricing = async (
  session: TSession,
): Promise<TCommunicationsPhoneNumberPricing> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/phone-number-pricing`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to fetch phone-number pricing");
  }

  return response.json();
};

export const listAvailablePhoneNumbers = async (
  session: TSession,
): Promise<TListAvailablePhoneNumbersResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/available-phone-numbers`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to list available phone numbers");
  }

  return response.json();
};

export const claimAvailablePhoneNumber = async (
  session: TSession,
  phoneNumberId: number,
  request: TClaimAvailablePhoneNumberRequest = {},
): Promise<TClaimAvailablePhoneNumberResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/available-phone-numbers/${phoneNumberId}/claim`,
    {
      method: "POST",
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to claim available phone number");
  }

  return response.json();
};

export const listAssignedPhoneNumbers = async (
  session: TSession,
): Promise<TListAssignedPhoneNumbersResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/phone-numbers`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to list assigned phone numbers");
  }

  return response.json();
};

export const setPrimaryPhoneNumber = async (
  session: TSession,
  phoneNumberId: number,
): Promise<TSetPrimaryPhoneNumberResponse> => {
  const clinicId = assertClinicId(session);
  const endpointCandidates: Array<{
    method: "POST" | "PATCH";
    path: string;
    body?: Record<string, unknown>;
  }> = [
    {
      method: "POST",
      path: `${urlPrefixCommunications}/${clinicId}/phone-numbers/${phoneNumberId}/set-primary`,
    },
    {
      method: "POST",
      path: `${urlPrefixCommunications}/${clinicId}/phone-numbers/${phoneNumberId}/primary`,
    },
    {
      method: "PATCH",
      path: `${urlPrefixCommunications}/${clinicId}/phone-numbers/${phoneNumberId}`,
      body: {
        is_primary: true,
      },
    },
  ];

  let lastError: Error | null = null;

  for (const endpoint of endpointCandidates) {
    const response = await fetchWithAuthRetry(endpoint.path, {
      method: endpoint.method,
      ...(endpoint.body
        ? {
            body: JSON.stringify(endpoint.body),
          }
        : {}),
    });

    if (response.ok) {
      const result =
        await parseJsonOrEmptyObject<TSetPrimaryPhoneNumberResponse>(response);
      return result as TSetPrimaryPhoneNumberResponse;
    }

    if (response.status !== 404 && response.status !== 405) {
      await throwHttpError(response, "Failed to set primary phone number");
    }

    lastError = new Error(
      `Failed to set primary phone number via ${endpoint.path} (${response.status}).`,
    );
  }

  throw (
    lastError ||
    new Error("Failed to set primary phone number for this clinic.")
  );
};

export const unassignPhoneNumber = async (
  session: TSession,
  phoneNumberId: number,
): Promise<TUnassignPhoneNumberResponse> => {
  const clinicId = assertClinicId(session);
  const normalizedPhoneNumberId = Number(phoneNumberId);
  if (!Number.isFinite(normalizedPhoneNumberId)) {
    throw new Error("Missing or invalid phone number ID for unassign.");
  }

  const endpointCandidates: Array<{
    method: "POST" | "DELETE";
    path: string;
  }> = [
    {
      method: "POST",
      path: `${urlPrefixCommunications}/${clinicId}/phone-numbers/${normalizedPhoneNumberId}/unassign`,
    },
    {
      method: "DELETE",
      path: `${urlPrefixCommunications}/${clinicId}/phone-numbers/${normalizedPhoneNumberId}`,
    },
    {
      method: "DELETE",
      path: `${urlPrefixCommunications}/${clinicId}/phone-numbers/${normalizedPhoneNumberId}/assignment`,
    },
  ];

  let lastError: Error | null = null;

  for (const endpoint of endpointCandidates) {
    const response = await fetchWithAuthRetry(endpoint.path, {
      method: endpoint.method,
    });

    if (response.ok) {
      const result =
        await parseJsonOrEmptyObject<TUnassignPhoneNumberResponse>(response);
      return result as TUnassignPhoneNumberResponse;
    }

    if (response.status !== 404 && response.status !== 405) {
      await throwHttpError(response, "Failed to unassign phone number");
    }

    lastError = new Error(
      `Failed to unassign phone number via ${endpoint.path} (${response.status}).`,
    );
  }

  throw lastError || new Error("Failed to unassign phone number.");
};

export const getInboundVoiceRouting = async (
  session: TSession,
  phoneNumberId: number,
): Promise<TInboundVoiceRoutingConfiguration> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/phone-numbers/${phoneNumberId}/inbound-voice-routing`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to fetch inbound voice routing");
  }

  const parsedResponse = await parseJsonOrEmptyObject<unknown>(response);
  return parseInboundVoiceRoutingConfiguration(parsedResponse);
};

export const updateInboundVoiceRouting = async (
  session: TSession,
  phoneNumberId: number,
  request: TInboundVoiceRoutingConfiguration,
): Promise<TInboundVoiceRoutingConfiguration> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/phone-numbers/${phoneNumberId}/inbound-voice-routing`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to update inbound voice routing");
  }

  const parsedResponse = await parseJsonOrEmptyObject<unknown>(response);
  const normalizedResponse =
    parseInboundVoiceRoutingConfiguration(parsedResponse);
  if (normalizedResponse.members.length || normalizedResponse.enabled) {
    return normalizedResponse;
  }
  return parseInboundVoiceRoutingConfiguration(request);
};

export const getCommunicationsUsage = async (
  session: TSession,
  request: TGetCommunicationsUsageRequest = {},
): Promise<TGetCommunicationsUsageResponse> => {
  const clinicId = assertClinicId(session);
  const queryParams = new URLSearchParams();

  if (request.start_date) {
    queryParams.set("start_date", request.start_date);
  }
  if (request.end_date) {
    queryParams.set("end_date", request.end_date);
  }
  if (request.group_by) {
    queryParams.set("group_by", request.group_by);
  }

  const queryString = queryParams.toString();
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/usage${queryString ? `?${queryString}` : ""}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to fetch communications usage");
  }

  return response.json();
};

export const listCommunicationsClinicUsersAccess = async (
  session: TSession,
): Promise<TListCommunicationsClinicUsersAccessResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/clinic-users/access`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(
      response,
      "Failed to list communications clinic user access",
    );
  }

  return response.json();
};

export const enableCommunicationsClinicUserAccess = async (
  session: TSession,
  userUuid: string,
): Promise<TUpdateCommunicationsClinicUserAccessResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/clinic-users/${encodeURIComponent(userUuid)}/access/enable`,
    {
      method: "POST",
    },
  );

  if (!response.ok) {
    await throwHttpError(
      response,
      "Failed to enable communications clinic user access",
    );
  }

  return response.json();
};

export const disableCommunicationsClinicUserAccess = async (
  session: TSession,
  userUuid: string,
): Promise<TUpdateCommunicationsClinicUserAccessResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/clinic-users/${encodeURIComponent(userUuid)}/access/disable`,
    {
      method: "POST",
    },
  );

  if (!response.ok) {
    await throwHttpError(
      response,
      "Failed to disable communications clinic user access",
    );
  }

  return response.json();
};

export const mintCommunicationsToken = async (
  session: TSession,
  request: TMintCommunicationsTokenRequest = {},
): Promise<TMintCommunicationsTokenResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/token`,
    {
      method: "POST",
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to mint communications token");
  }

  return response.json();
};

export const createOutboundCall = async (
  session: TSession,
  request: TCreateOutboundCallRequest,
): Promise<TCreateOutboundCallResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/calls/outbound`,
    {
      method: "POST",
      headers: buildCallAttemptHeaders(request.client_request_id),
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to create outbound call");
  }

  return response.json();
};

export const createOutboundMessage = async (
  session: TSession,
  request: TCreateOutboundMessageRequest,
): Promise<TCreateOutboundMessageResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/messages/outbound`,
    {
      method: "POST",
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to create outbound message");
  }

  return response.json();
};

export const listConversations = async (
  session: TSession,
  params: { limit?: number; offset?: number } = {},
): Promise<TListConversationsResponse> => {
  const clinicId = assertClinicId(session);
  const queryParams = new URLSearchParams();
  queryParams.set("limit", String(params.limit ?? 50));
  queryParams.set("offset", String(params.offset ?? 0));

  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/conversations?${queryParams.toString()}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to list conversations");
  }

  return response.json();
};

export const getConversation = async (
  session: TSession,
  conversationUuid: string,
): Promise<TGetConversationResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/conversations/${encodeURIComponent(conversationUuid)}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to fetch conversation");
  }

  return response.json();
};

export const markConversationRead = async (
  session: TSession,
  conversationUuid: string,
): Promise<void> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/conversations/${encodeURIComponent(conversationUuid)}/mark-read`,
    {
      method: "POST",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to mark conversation as read");
  }
};

export const markConversationUnread = async (
  session: TSession,
  conversationUuid: string,
): Promise<void> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/conversations/${encodeURIComponent(conversationUuid)}/mark-unread`,
    {
      method: "POST",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to mark conversation as unread");
  }
};

export const listConversationMessages = async (
  session: TSession,
  conversationUuid: string,
  params: { limit?: number; offset?: number } = {},
): Promise<TListConversationMessagesResponse> => {
  const clinicId = assertClinicId(session);
  const queryParams = new URLSearchParams();
  queryParams.set("limit", String(params.limit ?? 100));
  queryParams.set("offset", String(params.offset ?? 0));

  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/conversations/${encodeURIComponent(conversationUuid)}/messages?${queryParams.toString()}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to list conversation messages");
  }

  return response.json();
};

export const assignConversationToPatient = async (
  session: TSession,
  conversationUuid: string,
  request: TAssignConversationPatientRequest,
): Promise<TAssignConversationPatientResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/conversations/${encodeURIComponent(conversationUuid)}/assign-patient`,
    {
      method: "POST",
      body: JSON.stringify({
        patient_uuid: request.patient_uuid,
        ...(request.bulk_update !== undefined
          ? {
              bulk_update: request.bulk_update,
            }
          : {}),
        ...(request.overwrite_existing_phone_number !== undefined
          ? {
              overwrite_existing_phone_number:
                request.overwrite_existing_phone_number,
            }
          : {}),
      }),
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to assign conversation to patient");
  }

  const responseText = await response.text().catch(() => "");
  if (!responseText) {
    return {};
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return { message: responseText };
  }
};

export const listCalls = async (
  session: TSession,
  params: { limit?: number; offset?: number } = {},
): Promise<TListCallsResponse> => {
  const clinicId = assertClinicId(session);
  const queryParams = new URLSearchParams();
  queryParams.set("limit", String(params.limit ?? 50));
  queryParams.set("offset", String(params.offset ?? 0));

  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/calls?${queryParams.toString()}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to list calls");
  }

  return response.json();
};

export const getCallDetails = async (
  session: TSession,
  callUuid: TCommunicationsCallId,
  options: {
    callAttemptId?: string | null;
  } = {},
): Promise<TGetCallDetailResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/calls/${encodeURIComponent(callUuid)}`,
    {
      method: "GET",
      headers: buildCallAttemptHeaders(options.callAttemptId),
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to fetch call details");
  }

  return response.json();
};

export const hangupCall = async (
  session: TSession,
  callUuid: TCommunicationsCallId,
  options: {
    callAttemptId?: string | null;
  } = {},
): Promise<THangupCallResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/calls/${encodeURIComponent(callUuid)}/hangup`,
    {
      method: "POST",
      headers: buildCallAttemptHeaders(options.callAttemptId),
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to request call hangup");
  }

  return response.json();
};

export const listRecordings = async (
  session: TSession,
  params: TListRecordingsRequest = {},
): Promise<TListRecordingsResponse> => {
  const clinicId = assertClinicId(session);
  const queryParams = new URLSearchParams();
  queryParams.set("limit", String(params.limit ?? 50));
  queryParams.set("offset", String(params.offset ?? 0));

  const normalizedCallUuid = (params.call_uuid || "").trim();
  if (normalizedCallUuid) {
    queryParams.set("call_uuid", normalizedCallUuid);
  }
  if (params.patient_uuid) {
    queryParams.set("patient_uuid", params.patient_uuid);
  }
  if (params.ingestion_status) {
    queryParams.set("ingestion_status", params.ingestion_status);
  }
  if (params.transcription_status) {
    queryParams.set("transcription_status", params.transcription_status);
  }

  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/recordings?${queryParams.toString()}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to list recordings");
  }

  return response.json();
};

export const getCallNotes = async (
  session: TSession,
  callUuid: TCommunicationsCallId,
): Promise<TGetCallNotesResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/calls/${encodeURIComponent(callUuid)}/notes`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to fetch call notes");
  }

  return response.json();
};

export const assignCallToPatient = async (
  session: TSession,
  callUuid: TCommunicationsCallId,
  request: TAssignCallPatientRequest,
): Promise<TAssignCallPatientResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixCommunications}/${clinicId}/calls/${encodeURIComponent(callUuid)}/assign-patient`,
    {
      method: "POST",
      body: JSON.stringify({
        patient_uuid: request.patient_uuid,
        ...(request.bulk_update !== undefined
          ? {
              bulk_update: request.bulk_update,
            }
          : {}),
        ...(request.overwrite_existing_phone_number !== undefined
          ? {
              overwrite_existing_phone_number:
                request.overwrite_existing_phone_number,
            }
          : {}),
      }),
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to assign call to patient");
  }

  const responseText = await response.text().catch(() => "");
  if (!responseText) {
    return {};
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return { message: responseText };
  }
};

export const getPatientAudioForCallDocs = async (
  session: TSession,
  patientUuid: string,
  neverExpire: boolean = false,
): Promise<TDocumentationAudioResponse> => {
  const clinicId = assertClinicId(session);
  const queryParams = new URLSearchParams();
  if (neverExpire) {
    queryParams.set("never_expire", "true");
  }

  const response = await fetchWithAuthRetry(
    `${urlPrefixDocumentation}/${clinicId}/patients/${patientUuid}/audio${queryParams.toString() ? `?${queryParams.toString()}` : ""}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to fetch patient audio");
  }

  return response.json();
};

export const getPatientNotesForCallDocs = async (
  session: TSession,
  patientUuid: string,
): Promise<TDocumentationPatientNote[]> => {
  const clinicId = assertClinicId(session);

  const response = await fetchWithAuthRetry(
    `${urlPrefixDocumentation}/${clinicId}/patients/${patientUuid}/notes`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to fetch patient notes");
  }

  return response.json();
};

export const getOrganizedClinicAudioForCalls = async (
  session: TSession,
  neverExpire: boolean = false,
): Promise<TOrganizedAudioResponse> => {
  const clinicId = assertClinicId(session);
  const queryParams = new URLSearchParams();
  if (neverExpire) {
    queryParams.set("never_expire", "true");
  }

  const response = await fetchWithAuthRetry(
    `${urlPrefixDocumentation}/${clinicId}/audio/organized${queryParams.toString() ? `?${queryParams.toString()}` : ""}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to fetch clinic organized audio");
  }

  return response.json();
};

export const getOrganizedClinicNotesForCalls = async (
  session: TSession,
): Promise<TOrganizedNotesResponse> => {
  const clinicId = assertClinicId(session);
  const response = await fetchWithAuthRetry(
    `${urlPrefixDocumentation}/${clinicId}/notes/organized`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to fetch clinic organized notes");
  }

  return response.json();
};

export const getClinicNotesFeedForCalls = async (
  session: TSession,
  options: TGetClinicNotesFeedOptions = {},
): Promise<TNotesFeedResponse> => {
  const clinicId = assertClinicId(session);
  const queryParams = new URLSearchParams();

  if (options.mode?.trim()) {
    queryParams.set("mode", options.mode.trim());
  }
  if (typeof options.limit === "number") {
    queryParams.set("limit", String(options.limit));
  }
  if (options.cursor?.trim()) {
    queryParams.set("cursor", options.cursor.trim());
  }
  if (typeof options.includePatientInfo === "boolean") {
    queryParams.set("include_patient_info", String(options.includePatientInfo));
  }
  if (typeof options.includeSignedPdfUrl === "boolean") {
    queryParams.set(
      "include_signed_pdf_url",
      String(options.includeSignedPdfUrl),
    );
  }
  if (options.createdFrom?.trim()) {
    queryParams.set("created_from", options.createdFrom.trim());
  }
  if (options.createdTo?.trim()) {
    queryParams.set("created_to", options.createdTo.trim());
  }
  if (Array.isArray(options.noteType)) {
    options.noteType
      .map((entry) => entry.trim())
      .filter(Boolean)
      .forEach((entry) => queryParams.append("note_type", entry));
  }
  if (options.patientQuery?.trim()) {
    queryParams.set("patient_query", options.patientQuery.trim());
  }

  const response = await fetchWithAuthRetry(
    `${urlPrefixDocumentation}/${clinicId}/notes/feed${queryParams.toString() ? `?${queryParams.toString()}` : ""}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    await throwHttpError(response, "Failed to fetch clinic notes feed");
  }

  return response.json();
};
